import React, { useCallback } from "react";
import TailwindFlex from "library/components/_tailwind/flex";
import { inject, observer } from "mobx-react";
import ChatStore from "../_stores/chat/ChatStore";
import EmojiPopover from "./chat-emoji-popover";
import TranslationPopover from "./chat-translation-popover";
import BroadcastChatFontSizePopover from "./chat-font-size-popover";
import TailwindButton from "library/components/_tailwind/button";
import { TFontSize } from "library/styles/tailwind-classnames";
import CustomIcon from "library/components/_tailwind/icon/icons";
import TailwindBox from "library/components/_tailwind/box";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import ChatRoomTopicPopover from "./chat-room-topic-popover";
import ChatRoomQuickText from "./chat-room-quick-text";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import LanguageStore from "library/core/stores/language/LanguageStore";

type Props = {
  chatStore?: ChatStore;
  nodeChatStore?: NodeChatStore;
  broadcastStore?: BroadcastStore;
  languageStore?: LanguageStore;
  inputRef: React.RefObject<HTMLTextAreaElement>;
};

const BroadcastChatInputOptions: React.ComponentType<Props> = ({
  chatStore,
  nodeChatStore,
  broadcastStore,
  languageStore,
  inputRef,
}) => {
  const { chatFontSize, setChatFontSize, message, setMessage } = chatStore!;
  const { currentChatLanguage } = nodeChatStore!;
  const {
    isLiveTranslationActive,
    isPushToTalkActive,
    isPushToTalkCoolingDown,
    startPushToTalk,
    stopPushToTalk,
    pttActiveRemainingMs,
    pttCooldownRemainingMs,
  } = broadcastStore!;
  const { intl } = languageStore!;

  const addEmoji = useCallback(
    (emoji: string) => {
      const _ref = inputRef as any;
      const inputEl: HTMLInputElement = _ref.current;
      const selectionStart = inputEl.selectionStart;
      let newMessage = message;
      if (selectionStart !== null) {
        const selectionEnd = inputEl.selectionEnd;
        const messageFirstPart = message.substr(0, selectionStart);
        if (selectionEnd !== null) {
          const messageSecondPart = message.substr(
            selectionStart,
            message.length
          );
          newMessage = messageFirstPart + emoji + messageSecondPart;
        } else {
          newMessage = message + emoji;
        }
      }
      inputEl.focus();
      setMessage(newMessage);
    },
    [message, inputRef]
  );

  const onSetFont = (font: number | TFontSize) => {
    setChatFontSize(font as number);
  };

  return (
    <TailwindFlex gap={"gap-2"}>
      <EmojiPopover onSelectEmoji={addEmoji}>
        <TailwindButton
          dataTestId={"chat-emoji-button"}
          type={"button"}
          fullWidth={false}
          backgroundColor={"bg-secondary-bg-color"}
          borderRadius={"rounded-full"}
          leftIconProps={{
            name: "tag_faces",
          }}
        />
      </EmojiPopover>
      <TranslationPopover>
        <TailwindButton
          dataTestId={"chat-translation-button"}
          type={"button"}
          fullWidth={false}
          backgroundColor={"bg-secondary-bg-color"}
          borderRadius={"rounded-full"}>
          {currentChatLanguage !== "en" ? (
            <TailwindBox
              as={"span"}
              height={"h-4"}
              width={"w-4"}
              className={["fi", `fi-${currentChatLanguage}`]}
              borderRadius={"rounded-full"}
              backgroundSize={"bg-cover"}
              backgroundPosition={"bg-center"}
              backgroundRepeat={"bg-no-repeat"}
            />
          ) : (
            <CustomIcon name={"translate"} />
          )}
        </TailwindButton>
      </TranslationPopover>
      {isLiveTranslationActive && (
        <TailwindButton
          dataTestId={"chat-translating-indicator-button"}
          type={"button"}
          fullWidth={false}
          backgroundColor={"bg-secondary-bg-color"}
          borderRadius={"rounded-full"}
          onClick={() => {
            if (isPushToTalkCoolingDown) return;
            if (isPushToTalkActive) {
              stopPushToTalk();
            } else {
              startPushToTalk();
            }
          }}
          disabled={isPushToTalkCoolingDown}>
          {isPushToTalkActive ? (
            <span>
              <span className='live-translation-ellipsis'>
                {intl
                  .formatMessage({
                    id: "broadcast.translating",
                    defaultMessage: "Translating",
                  })
                  .split("")
                  .map((char, index) => (
                    <span key={index} className='dot'>
                      {char}
                    </span>
                  ))}
                <span className='dot'>.</span>
                <span className='dot'>.</span>
                <span className='dot'>.</span>
              </span>
              {` (${Math.max(
                0,
                Math.ceil((pttActiveRemainingMs || 0) / 1000)
              )}s)`}
            </span>
          ) : (
            <span className='flex items-center gap-1'>
              <span style={{ marginTop: "1px" }}>
                <CustomIcon name={"mic"} />
              </span>
              <span className='inline'>
                {intl.formatMessage({
                  id: "broadcast.translate",
                  defaultMessage: "Translate",
                })}
              </span>
              {isPushToTalkCoolingDown
                ? ` (${Math.max(
                    0,
                    Math.ceil((pttCooldownRemainingMs || 0) / 1000)
                  )}s)`
                : ""}
            </span>
          )}
        </TailwindButton>
      )}
      <BroadcastChatFontSizePopover
        fontSize={chatFontSize}
        onSetFont={onSetFont}>
        <TailwindButton
          dataTestId={"chat-font-size-button"}
          type={"button"}
          fullWidth={false}
          backgroundColor={"bg-secondary-bg-color"}
          borderRadius={"rounded-full"}
          leftIconProps={{
            name: "text_fields",
          }}
        />
      </BroadcastChatFontSizePopover>
      <ChatRoomTopicPopover>
        <TailwindButton
          dataTestId={"chat-topic-button"}
          type={"button"}
          fullWidth={false}
          backgroundColor={"bg-secondary-bg-color"}
          borderRadius={"rounded-full"}
          leftIconProps={{
            name: "double-hearts",
          }}
        />
      </ChatRoomTopicPopover>
      <ChatRoomQuickText>
        <TailwindButton
          dataTestId={"chat-quick-text-button"}
          type={"button"}
          fullWidth={false}
          backgroundColor={"bg-secondary-bg-color"}
          borderRadius={"rounded-full"}
          leftIconProps={{
            name: "chat",
          }}
        />
      </ChatRoomQuickText>
    </TailwindFlex>
  );
};

export default inject(
  "chatStore",
  "nodeChatStore",
  "broadcastStore",
  "languageStore"
)(observer(BroadcastChatInputOptions));
